import { FunctionComponent, Suspense, useCallback, useEffect, useRef } from 'react';
import { Footer } from './layout/Footer';
import { BrowserSolidLdoProvider, useResource, useSolidAuth } from '@ldo/solid-react';
import { SolidContainer, SolidContainerUri, SolidLeaf, SolidLeafUri } from '@ldo/connected-solid';
import { useEffectLoadFullProfile, useLoadPreferences } from './lib/profile';
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { Home } from './pages/Home';
import ReactModal from 'react-modal';
import { AppContext, SparqlEngine, useAppContext } from './components/AppContext';
import { Loading } from './components/Loading';
import i18n from "i18next";
import Backend from 'i18next-http-backend';
import { initReactI18next, useTranslation } from 'react-i18next';
import { NewWorkspace } from './pages/NewWorkspace';
import { WorkspacePage } from './pages/WorkspacePage';
import { LanguagePage } from './pages/LanguagePage';
import { LanguagesPage } from './pages/LanguagesPage';
import { StimuliPage } from './pages/StimuliPage';
import { StimulusPage } from './pages/StimulusPage';
import { SentencePage } from './pages/SentencePage';
import { SparnaturalPage } from './pages/SparnaturalPage';
import { MapPage } from './pages/MapPage';
import { cap } from './lib/cap';
import { getBrowserLanguage } from "./components/AppContext"
import { WorkspaceLayout } from './layout/WorkspaceLayout';
import appInfo from "./app-info.json";
// CSS
import "./App.css";

// Save the current URL before any OIDC redirect so we can restore it afterwards.
// sessionStorage is per-tab, so this is safe with multiple tabs open.
const RETURN_URL_KEY = 'spoty-return-url';
(function saveReturnUrl() {
  if (window.location.search.includes('code=')) return; // already an OIDC callback
  if (window.location.pathname === '/') return;         // home page, nothing to restore
  sessionStorage.setItem(RETURN_URL_KEY, window.location.pathname + window.location.search + window.location.hash);
})();

i18n
  .use(Backend) // will load translations from public/locales
  .use(initReactI18next)
  .init({
    lng: getBrowserLanguage() ?? "en",
    fallbackLng: "en",

    interpolation: {
    escapeValue: false // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
    }
  });

// Main application component.
//
// Wraps everything else into an `AppContext` and a `BrowserRouter`.
//
// The real thing is implemented in `AppInner`,
// so that the `AppContext` and `BrowserRouter` features can be used.
export const App: FunctionComponent = () => {
  console.debug("App: rendering");
  ReactModal.setAppElement("#root");

  return (
    <div className="App">
      <Suspense fallback={<Loading>i18n files</Loading>}>
        <BrowserSolidLdoProvider>
          <AppContext>
            <BrowserRouter>
              <AppInner />
            </BrowserRouter>
          </AppContext>
        </BrowserSolidLdoProvider>
      </Suspense>
    </div>
  );
}


// Creates the general app layout (see `Header` and `Footer`) and the defines the routes.
const AppInner: FunctionComponent = () => {
  const { session } = useSolidAuth();
  const [ appCtx, dispatch ] = useAppContext();
  console.log("App: context is", appCtx);
  useEffectLoadFullProfile(session.webId, appCtx.loginInProgress && session.isLoggedIn, () => dispatch({ type: "unsetLoginInProgress" }));
  const podPreferences = useLoadPreferences(session.webId);

  useEffect(() => {
    console.debug("Apps's effet: create Oxigraph worker for sparqlEngine");

    const worker = new Worker(new URL('./workers/oxigraph.worker.ts', import.meta.url));
    let sparqlEngine: SparqlEngine | null = null;
    const pendingQueries = new Map<string, { callback: (data: any) => void, errorCallback: (error: any) => void }>();
    let queryId = 0;

    worker.onmessage = (e: MessageEvent) => {
      const msg = e.data;
      if (!sparqlEngine && msg.type === 'ready') {
        sparqlEngine = {
          clear: (callback, errorCallback) => {
              const id = String(++queryId);
              pendingQueries.set(id, { callback, errorCallback });
              worker.postMessage({ type: 'clear', id });
          },
          populate: (quads, callback, errorCallback) => {
              const id = String(++queryId);
              pendingQueries.set(id, { callback, errorCallback });
              worker.postMessage({ type: 'populate', id, quads });
          },
          executeSparql: (sparql, callback, errorCallback) => {
              console.debug("sparnatural.executeSparql");
              const id = String(++queryId);
              pendingQueries.set(id, { callback, errorCallback });
              worker.postMessage({ type: 'query', id, sparql });
          },
        };
        dispatch({ type: 'setSparqlEngine', sparqlEngine });
        return;
      }
      const pending = pendingQueries.get(msg.id);
      if (pending) {
        pendingQueries.delete(msg.id);
        switch (msg.type) {
          case 'cleared': {
            pending.callback(null);
            break;
          }
          case 'populated': {
            pending.callback(msg.size);
            break;
          }
          case 'result': {
            pending.callback(msg.results);
            break;
          }
          case 'error': {
            pending.errorCallback(msg.results);
            break;
          }
          default: {
            console.warn("oxigraph worker responded with unknwon message type", msg.type);
          }
        }
      } else {
        console.warn("oxigraph worker responded with unknwon query.id", msg.id);
      }
    };

    return () => {
        worker.terminate();
    };
  }, [dispatch]);

  useEffect(() => {
    if (podPreferences) {
      dispatch({ type: "setPreferences", preferences: podPreferences });
      i18n.changeLanguage(podPreferences.language);
    }
  }, [podPreferences, dispatch]);

  const navigate = useNavigate();

  // After a login redirect, navigate back to the page the user originally requested.
  const prevLoginInProgress = useRef(appCtx.loginInProgress);
  useEffect(() => {
    const wasInProgress = prevLoginInProgress.current;
    prevLoginInProgress.current = appCtx.loginInProgress;
    if (wasInProgress && !appCtx.loginInProgress) {
      const returnUrl = sessionStorage.getItem(RETURN_URL_KEY);
      if (returnUrl) {
        sessionStorage.removeItem(RETURN_URL_KEY);
        navigate(returnUrl);
      }
    }
  }, [appCtx.loginInProgress, navigate]);

  const gotoWorkspace = useCallback((workspace: SolidContainer) => {
    navigate(`/w/${encodeURIComponent(workspace.uri)}/`);
  }, [navigate]);

  const ontology = useResource(ONTOLOGY);
  const languages = useResource(LANGUAGES);

  const { t } = useTranslation(["translation", "spoty"]);

  if (!ontology.isFetched() && !languages.isFetched()) {
    return <>
      <Loading asPage={appInfo.name}>
        {cap(t("loading ontology", { ns: 'spoty' }))}
      </Loading>
      <Footer />
    </>;
  }

  return (<>
    {
      appCtx.loginInProgress
      ? <Loading asPage={appInfo.name}>{cap(t("login in progress"))}</Loading>
      : <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/w/new" element={<NewWorkspace onCreate={gotoWorkspace} />} />
          <Route path="/w/:wid/" element={<WorkspaceLayout />}>
            <Route path="" element={<WorkspacePage />} />
            <Route path="lang/" element={<LanguagesPage />} />
            <Route path="lang/:lid" element={<LanguagePage />} />
            <Route path="map" element={<MapPage/>} />
            <Route path="stim/" element={<StimuliPage/>} />
            <Route path="stim/:sid" element={<StimulusPage/>} />
            <Route path="s/:sid/" element={<SentencePage/>} />
            <Route path="sparnatural" element={<SparnaturalPage/>} />
          </Route>
          <Route path="/s/:sid/" element={<SentencePage/>} />
          <Route path="/lang/:lid" element={<LanguagePage />} />
          <Route path="/stim/:sid" element={<StimulusPage />} />
      </Routes>
    }
    <Footer />
  </>);
}

export const ONTOLOGY = "" + new URL(process.env.PUBLIC_URL+"/assets/rdf/ontology.ttl", window.location.href);
export const LANGUAGES = "" + new URL(process.env.PUBLIC_URL+"/assets/rdf/languages.ttl", window.location.href);

export type Workspace = SolidContainer | SolidLeaf;
export type WorkspaceUri = SolidContainerUri | SolidLeafUri;
